import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Typography, 
  Row, 
  Col, 
  Space, 
  Progress, 
  Result,
  Statistic,
  Divider,
  message,
  Modal,
  Tag,
  List,
  Spin
} from 'antd';
import { 
  TrophyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  HomeOutlined,
  RedoOutlined,
  StarFilled
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useSecurityProtection } from '../hooks/useSecurityProtection';
import { submitExamResult, useUserExamResultDetails } from '../hooks/exam';
import authService from '../services/authService';
import { THEME_COLORS, GRADIENTS, COMMON_STYLES } from '../utils/theme';

const { Title, Text } = Typography;

interface ExamResultData {
  examTitle: string;
  totalQuestions: number;
  correctAnswers: number;
  score: number;
  passed: boolean;
  userAnswers: Record<string, any>;
  examData: any;
}

const ExamResult: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const location = useLocation();
  useSecurityProtection();
  const { examId } = useParams<{ examId: string }>();
  const [resultSubmitted, setResultSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showIncorrectAnswers, setShowIncorrectAnswers] = useState(false);
  
  // Get current user
  const currentUser = authService.getCurrentUser();
  const userId = currentUser?.id?.toString();
  
  // Fetch user exam result details from API
  const { data: examResultDetails, isLoading: detailsLoading } = useUserExamResultDetails(examId, userId);
  
  // Get result data from navigation state
  const resultData: ExamResultData = location.state || {};
  const {
    examTitle = 'Mock Exam 1',
    totalQuestions = 0,
    correctAnswers = 0,
    score = 0,
    passed = false,
    userAnswers = {},
    examData = {}
  } = resultData;

  // Calculate additional metrics
  const incorrectAnswers = totalQuestions - correctAnswers;
  const accuracyPercentage = totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
  const passMarks = examData.passMarks/totalQuestions * 100 || 70;
  const FinalScore = score ;


  // Determine result status and styling
  const getResultConfig = () => {
    if (passed) {
      return {
        status: 'success' as const,
        title: 'Congrats!',
        subTitle: 'You have successfully passed the exam',
        icon: <TrophyOutlined style={{ color: THEME_COLORS.success }} />,
        color: THEME_COLORS.success,
        bgGradient: GRADIENTS.success
      };
    } else {
      return {
        status: 'error' as const,
        title: 'Better luck next time!',
        subTitle: 'You need more preparation to pass this exam',
        icon: <CloseCircleOutlined style={{ color: THEME_COLORS.error }} />,
        color: THEME_COLORS.error,
        bgGradient: GRADIENTS.primaryBlue
      };
    }
  };

  const resultConfig = getResultConfig();

  // Submit exam result to API when component mounts
  // useEffect(() => {
  //   const submitResult = async () => {
  //     // Check if this result was already submitted to avoid duplicates
  //     const submittedKey = `exam_result_submitted_${examId}`;
  //     const alreadySubmittedToAPI = sessionStorage.getItem(submittedKey);
      
  //     if (resultSubmitted || submitting || !examId || !resultData || Object.keys(resultData).length === 0 || alreadySubmittedToAPI) {
  //       return;
  //     }

  //     setSubmitting(true);
  //     try {
  //       const currentUser = authService.getCurrentUser();
  //       const userId = currentUser?.id || 1;

  //       const examResultData = {
  //         user_id: parseInt(userId.toString()),
  //         exam_id: parseInt(examId),
  //         score: Math.round(score),
  //         total_score: totalQuestions,  
  //         passed: passed ? 1 : 0
  //       };

  //       console.log('Submitting exam result:', examResultData);
  //       await submitExamResult(examResultData);
        
  //       setResultSubmitted(true);
  //       // Mark this result as submitted in session storage
  //       sessionStorage.setItem(submittedKey, 'true');
  //       console.log('Exam result submitted successfully');
        
  //       // Optional: Show success message
  //       // message.success('Exam result saved successfully');
  //     } catch (error: any) {
  //       console.error('Failed to submit exam result:', error);
  //       // Optional: Show error message
  //       // message.error('Failed to save exam result');
  //     } finally {
  //       setSubmitting(false);
  //     }
  //   };

  //   // Delay submission slightly to ensure component is fully mounted
  //   const timer = setTimeout(() => {
  //     submitResult();
  //   }, 1000);

  //   return () => clearTimeout(timer);
  // }, [examId, score, passed, resultData, resultSubmitted, submitting, totalQuestions]);

  const handleRetakeExam = () => {
    navigate(`/exam/${examId}`, { 
      state: examData 
    });
  };

  const handleGoToDashboard = () => {
    window.scrollTo(0, 0);
    navigate('/profile', { state: { activeTab: 'quizDetails' } });
  };

  const handleViewExamDetails = () => {
    navigate(`/exam/${examId}`);
  };

  // Get incorrect answers details from API (where point === 0)
  const getIncorrectAnswersDetails = () => {
    if (!examResultDetails || examResultDetails.length === 0) return [];
    
    // Filter only incorrect answers (point === 0)
    const incorrectAnswers = examResultDetails.filter((item: any) => item.point === 0);
    
    // Map to the format needed for display with proper sequential numbering
    return incorrectAnswers.map((item: any, index: number) => {
      // Find the correct answer from answer_options
      const correctOption = item.answer_options?.find((opt: any) => opt.is_correct === 1);
      
      // Find user's selected answer (if any)
      const userSelectedOption = item.answer_options?.find((opt: any) => 
        userAnswers[item.question_id]?.answerId === opt.id.toString()
      );
      
      return {
        questionNumber: index + 1, // Sequential numbering for incorrect answers display
        questionId: item.question_id,
        question: item.question_text,
        userAnswer: userSelectedOption?.option_text || 'Not answered',
        correctAnswer: correctOption?.option_text || 'N/A',
        options: item.answer_options?.map((opt: any) => ({
          id: opt.id,
          text: opt.option_text,
          isCorrect: opt.is_correct === 1,
          isUserAnswer: userAnswers[item.question_id]?.answerId === opt.id.toString()
        })) || [],
        explanation: item.explanation || null
      };
    });
  };

  const incorrectAnswersDetails = getIncorrectAnswersDetails();
  

  // If no result data, redirect to dashboard
  if (!resultData || Object.keys(resultData).length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        textAlign: 'center'
      }}>
        <Card style={{ padding: '40px' }}>
          <Text>No exam result data found.</Text>
          <br />
          <Button type="primary" onClick={handleGoToDashboard} style={{ marginTop: '16px' }}>
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      maxWidth: COMMON_STYLES.containerMaxWidth,
      margin: '0 auto',
      padding: isMobile ? '16px' : '24px',
      minHeight: '100vh',
      backgroundColor: THEME_COLORS.gray3
    }}>
      {/* Header Card with Result Status */}
      <Card 
        style={{ 
          borderRadius: COMMON_STYLES.cardBorderRadius,
          border: 'none',
          background: resultConfig.bgGradient,
          color: THEME_COLORS.textLight,
          marginBottom: '24px',
          textAlign: 'center',
          boxShadow: COMMON_STYLES.cardShadow
        }}
      >
        <div style={{ padding: isMobile ? '32px 20px' : '48px 32px' }}>
          <div style={{ 
            fontSize: isMobile ? '56px' : '72px',
            marginBottom: '16px',
            lineHeight: 1
          }}>
            {passed ? '🏆' : '📚'}
          </div>
          
          <Title 
            level={2} 
            style={{ 
              color: THEME_COLORS.textLight, 
              margin: '0 0 12px 0',
              fontSize: isMobile ? '24px' : '32px',
              fontWeight: 600
            }}
          >
            {resultConfig.title}
          </Title>
          
          <Text style={{ 
            color: 'rgba(255,255,255,0.9)',
            fontSize: isMobile ? '14px' : '16px',
            display: 'block',
            marginBottom: '24px'
          }}>
            {resultConfig.subTitle}
          </Text>
          
          <div style={{ marginTop: '32px' }}>
            <Title level={1} style={{ 
              color: THEME_COLORS.textLight, 
              margin: 0,
              fontSize: isMobile ? '48px' : '64px',
              fontWeight: 700,
              lineHeight: 1
            }}>
              {accuracyPercentage.toFixed(0)}%
            </Title>
            <Text style={{ 
              color: 'rgba(255,255,255,0.85)',
              fontSize: isMobile ? '13px' : '14px',
              display: 'block',
              marginTop: '8px'
            }}>
              Final Score
            </Text>
          </div>
        </div>
      </Card>

      {/* Color Palette Display */}
      {/* <Card 
        style={{ 
          borderRadius: COMMON_STYLES.cardBorderRadius,
          marginBottom: '24px',
          border: 'none',
          boxShadow: COMMON_STYLES.cardShadow
        }}
      >
        <Space direction="horizontal" size="middle" style={{ width: '100%', justifyContent: 'center', flexWrap: 'wrap' }}>
          {[
            { color: THEME_COLORS.primaryDark, label: '182867' },
            { color: THEME_COLORS.primaryBlue, label: '2c479e' },
            { color: THEME_COLORS.success, label: '00a79d' },
            { color: THEME_COLORS.error, label: 'da1f3d' },
            { color: THEME_COLORS.accentCyan, label: '44c8f5' },
            { color: THEME_COLORS.gray4, label: 'f5fdff' },
          ].map((item, index) => (
            <div 
              key={index}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px'
              }}
            >
              <div 
                style={{ 
                  width: '36px', 
                  height: '36px', 
                  backgroundColor: item.color,
                  borderRadius: '8px',
                  border: `1px solid ${THEME_COLORS.gray2}`
                }} 
              />
              <Text style={{ fontFamily: 'monospace', fontSize: '14px' }}>{item.label}</Text>
            </div>
          ))}
        </Space>
      </Card> */}

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={6}>
          <Card style={{ borderRadius: COMMON_STYLES.buttonBorderRadius, textAlign: 'center', border: 'none', boxShadow: COMMON_STYLES.cardShadow }}>
            <Statistic
              title="Total Questions"
              value={totalQuestions}
              prefix={<CheckCircleOutlined style={{ color: THEME_COLORS.primaryBlue }} />}
              valueStyle={{ color: THEME_COLORS.primaryBlue, fontSize: isMobile ? '20px' : '24px', fontWeight: 600 }}
            />
          </Card>
        </Col>
        
        <Col xs={12} sm={6}>
          <Card style={{ borderRadius: COMMON_STYLES.buttonBorderRadius, textAlign: 'center', border: 'none', boxShadow: COMMON_STYLES.cardShadow }}>
            <Statistic
              title="Correct Answers"
              value={correctAnswers}
              prefix={<CheckCircleOutlined style={{ color: THEME_COLORS.success }} />}
              valueStyle={{ color: THEME_COLORS.success, fontSize: isMobile ? '20px' : '24px', fontWeight: 600 }}
            />
          </Card>
        </Col>
        
        <Col xs={12} sm={6}>
          <Card 
            style={{ 
              borderRadius: COMMON_STYLES.buttonBorderRadius, 
              textAlign: 'center', 
              border: 'none', 
              boxShadow: COMMON_STYLES.cardShadow,
              cursor: incorrectAnswers > 0 ? 'pointer' : 'default',
              transition: 'all 0.3s ease'
            }}
            hoverable={incorrectAnswers > 0}
            onClick={() => incorrectAnswers > 0 && setShowIncorrectAnswers(true)}
          >
            <Statistic
              title="Incorrect Answers"
              value={incorrectAnswers}
              prefix={<CloseCircleOutlined style={{ color: THEME_COLORS.error }} />}
              valueStyle={{ color: THEME_COLORS.error, fontSize: isMobile ? '20px' : '24px', fontWeight: 600 }}
            />
            {/* {incorrectAnswers > 0 && (
              <Text style={{ fontSize: '12px', color: THEME_COLORS.primaryBlue, marginTop: '8px', display: 'block' }}>
                Click to view details
              </Text>
            )} */}
          </Card>
        </Col>
        
        <Col xs={12} sm={6}>
          <Card style={{ borderRadius: COMMON_STYLES.buttonBorderRadius, textAlign: 'center', border: 'none', boxShadow: COMMON_STYLES.cardShadow }}>
            <Statistic
              title="Accuracy"
              value={accuracyPercentage.toFixed(1)}
              suffix="%"
              prefix={<StarFilled style={{ color: THEME_COLORS.accentCyan }} />}
              valueStyle={{ color: THEME_COLORS.accentCyan, fontSize: isMobile ? '20px' : '24px', fontWeight: 600 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Detailed Results Card */}
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TrophyOutlined style={{ marginRight: '8px', color: resultConfig.color }} />
            Exam Details
          </div>
        }
        style={{ 
          borderRadius: COMMON_STYLES.cardBorderRadius,
          marginBottom: '24px',
          border: 'none',
          boxShadow: COMMON_STYLES.cardShadow
        }}
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12}>
            <div style={{ marginBottom: '16px' }}>
              <Text strong style={{ color: '#8c8c8c' }}>Exam Title:</Text>
              <br />
              <Text style={{ fontSize: '16px' }}>{examTitle}</Text>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <Text strong style={{ color: '#8c8c8c' }}>Pass Marks:</Text>
              <br />
              <Text style={{ fontSize: '16px' }}>{passMarks.toFixed(1)}%</Text>
            </div>
          </Col>
          
          <Col xs={24} sm={12}>
            <div style={{ marginBottom: '16px' }}>
              <Text strong style={{ color: THEME_COLORS.textSecondary }}>Your Score:</Text>
              <br />
              <Text style={{ 
                fontSize: '16px',
                color: passed ? THEME_COLORS.success : THEME_COLORS.error,
                fontWeight: 'bold'
              }}>
                {accuracyPercentage.toFixed(1)}%
              </Text>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <Text strong style={{ color: THEME_COLORS.textSecondary }}>Result:</Text>
              <br />
              <Text style={{ 
                fontSize: '16px',
                color: passed ? THEME_COLORS.success : THEME_COLORS.error,
                fontWeight: 'bold'
              }}>
                {passed ? 'PASSED' : 'FAILED'}
              </Text>
            </div>
          </Col>
        </Row>

        <Divider />

        {/* Progress Bar */}
        <div style={{ marginBottom: '20px' }}>
          <Text strong style={{ marginBottom: '8px', display: 'block' }}>
            Score Progress
          </Text>
          <Progress
            percent={accuracyPercentage.toFixed(0) as unknown as number}
            strokeColor={passed ? THEME_COLORS.success : THEME_COLORS.error}
            trailColor={THEME_COLORS.gray2}
            strokeWidth={12}
            style={{ marginBottom: '8px' }}
          />
          <Row justify="space-between">
            <Text style={{ fontSize: '12px', color: THEME_COLORS.textSecondary }}>0%</Text>
            <Text style={{ fontSize: '12px', color: THEME_COLORS.textSecondary }}>
              Pass: {passMarks.toFixed(1)}%
            </Text>
            <Text style={{ fontSize: '12px', color: THEME_COLORS.textSecondary }}>100%</Text>
          </Row>
        </div>
      </Card>

      {/* Action Buttons */}
      <Row gutter={[16, 16]} justify="center">
        <Col xs={24} sm={8}>
          <Button
            type="primary"
            size="large"
            icon={<HomeOutlined />}
            onClick={handleGoToDashboard}
            style={{
              width: '100%',
              height: '48px',
              borderRadius: COMMON_STYLES.buttonBorderRadius,
              backgroundColor: THEME_COLORS.primaryBlue,
              borderColor: THEME_COLORS.primaryBlue,
              fontWeight: 600
            }}
          >
            Go to Dashboard
          </Button>
        </Col>
        {incorrectAnswers > 0 && (
          <Col xs={24} sm={8}>
            <Button
              size="large"
              icon={<CloseCircleOutlined />}
              onClick={() => setShowIncorrectAnswers(true)}
              loading={detailsLoading}
              style={{
                width: '100%',
                height: '48px',
                borderRadius: COMMON_STYLES.buttonBorderRadius,
                borderColor: THEME_COLORS.error,
                color: THEME_COLORS.error,
                fontWeight: 600
              }}
            >
              View Incorrect Answers ({incorrectAnswers})
            </Button>
          </Col>
        )}
      </Row>

      {/* Performance Insight */}
      {!passed && (
        <Card 
          style={{ 
            borderRadius: COMMON_STYLES.cardBorderRadius,
            marginTop: '24px',
            backgroundColor: '#E6F7FF',
            border: 'none',
            boxShadow: COMMON_STYLES.cardShadow
          }}
        >
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <Text style={{ fontSize: '16px', color: THEME_COLORS.primaryBlue, lineHeight: '1.6' }}>
              💡 <strong>Study Tip:</strong> You answered {correctAnswers} out of {totalQuestions} questions correctly. 
              Focus on reviewing the topics you missed and try again when you're ready!
            </Text>
          </div>
        </Card>
      )}

      {/* Incorrect Answers Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CloseCircleOutlined style={{ marginRight: '8px', color: THEME_COLORS.error, fontSize: '20px' }} />
            <span>Incorrect Answers Review</span>
          </div>
        }
        open={showIncorrectAnswers}
        onCancel={() => setShowIncorrectAnswers(false)}
        footer={[
          <Button 
            key="close" 
            type="primary" 
            onClick={() => setShowIncorrectAnswers(false)}
            style={{
              backgroundColor: THEME_COLORS.primaryBlue,
              borderColor: THEME_COLORS.primaryBlue,
              borderRadius: COMMON_STYLES.buttonBorderRadius
            }}
          >
            Close
          </Button>
        ]}
        width={isMobile ? '100%' : 800}
        style={{ top: isMobile ? 0 : 20 }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <div style={{ padding: isMobile ? '8px' : '16px' }}>
          <Text style={{ fontSize: '14px', color: THEME_COLORS.textSecondary, marginBottom: '16px', display: 'block' }}>
            Review the questions you answered incorrectly. Study the correct answers to improve your understanding.
          </Text>
          
          {detailsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
            </div>
          ) : incorrectAnswersDetails.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Text>No incorrect answers found or data not available yet.</Text>
            </div>
          ) : (
            <List
              dataSource={incorrectAnswersDetails}
              renderItem={(item: any) => (
                <Card 
                  style={{ 
                    marginBottom: '16px',
                    border: `1px solid ${THEME_COLORS.gray2}`,
                    borderRadius: COMMON_STYLES.cardBorderRadius,
                    boxShadow: 'none'
                  }}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <Tag color="red" style={{ marginBottom: '8px' }}>Question {item.questionNumber}</Tag>
                    <div 
                      dangerouslySetInnerHTML={{ __html: item.question }}
                      style={{ 
                        fontSize: '16px', 
                        marginBottom: '12px', 
                        color: THEME_COLORS.textPrimary,
                        fontWeight: 600
                      }}
                    />
                  </div>

                  {item.options && item.options.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        {item.options.map((option: any, idx: number) => {
                          return (
                            <div 
                              key={option.id}
                              style={{
                                padding: '12px 16px',
                                borderRadius: '8px',
                                backgroundColor: option.isCorrect
                                  ? 'rgba(0, 167, 157, 0.1)' 
                                  : option.isUserAnswer
                                  ? 'rgba(218, 31, 61, 0.1)' 
                                  : 'transparent',
                                border: option.isCorrect
                                  ? `2px solid ${THEME_COLORS.success}` 
                                  : option.isUserAnswer
                                  ? `2px solid ${THEME_COLORS.error}` 
                                  : `1px solid ${THEME_COLORS.gray2}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                              }}
                            >
                              <Text style={{ flex: 1, fontSize: '15px' }}>{option.text}</Text>
                              {option.isCorrect && (
                                <Tag color="success" icon={<CheckCircleOutlined />}>Correct</Tag>
                              )}
                            </div>
                          );
                        })}
                      </Space>
                    </div>
                  )}

                <Divider style={{ margin: '16px 0' }} />

                <div style={{ 
                  padding: '14px',
                  backgroundColor: 'rgba(0, 167, 157, 0.05)',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${THEME_COLORS.success}`
                }}>
                  <Space direction="vertical" size={4}>
                    <Text strong style={{ color: THEME_COLORS.success, fontSize: '13px' }}>
                      <CheckCircleOutlined /> Correct Answer:
                    </Text>
                    <Text style={{ fontSize: '15px', color: THEME_COLORS.textPrimary }}>
                      {item.correctAnswer}
                    </Text>
                  </Space>
                </div>

                {item.explanation && (
                  <div style={{ 
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#E6F7FF',
                    borderRadius: '6px',
                    borderLeft: `4px solid ${THEME_COLORS.primaryBlue}`
                  }}>
                    <Text strong style={{ color: THEME_COLORS.primaryBlue, display: 'block', marginBottom: '4px' }}>
                      💡 Explanation:
                    </Text>
                    <Text style={{ fontSize: '14px', color: THEME_COLORS.textSecondary }}>
                      {item.explanation}
                    </Text>
                  </div>
                )}
                </Card>
              )}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ExamResult;